import * as React from 'react';
import { Bell, CheckCircle2, FileText, ListChecks, ShieldCheck } from '@/components/icons';
import type { Notification } from '@senviro/core-api-client';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { bucketFor } from '@/features/notifications/group-by-entity';
import {
  useAcceptInvitation,
  useDeclineInvitation,
  type InvitationAction,
} from '@/features/notifications/api/use-invitation-actions';

export interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  /** Optional click handler (e.g. navigate to entity_url). */
  onSelect?: (n: Notification) => void;
}

/**
 * Shape of a single W17-F4 action button as carried inside
 * `notification.extra_data.actionButtons`. We accept a permissive subset
 * here and validate at runtime so a malformed payload from the author
 * surface degrades gracefully instead of blowing up the row.
 */
interface ActionButtonPayload {
  label: string;
  url: string;
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  variant?: 'primary' | 'outline';
  body?: unknown;
}

function readActionButtons(n: Notification): ActionButtonPayload[] {
  const extra = n.extra_data as { actionButtons?: unknown } | undefined;
  const raw = extra?.actionButtons;
  if (!Array.isArray(raw)) return [];
  const out: ActionButtonPayload[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.label !== 'string' || typeof e.url !== 'string') continue;
    const method =
      typeof e.method === 'string' &&
      ['POST', 'PUT', 'PATCH', 'DELETE'].includes(e.method)
        ? (e.method as ActionButtonPayload['method'])
        : 'POST';
    const variant =
      e.variant === 'outline' ? ('outline' as const) : ('primary' as const);
    out.push({
      label: e.label,
      url: e.url,
      method,
      variant,
      body: e.body,
    });
  }
  return out;
}

const KIND_ICON: Record<string, React.FC<{ className?: string }>> = {
  'task.assigned': ListChecks,
  'task.created': ListChecks,
  'task.status_changed': ListChecks,
  'evidence.uploaded': FileText,
  'evaluation.completed': CheckCircle2,
  'notification.created': Bell,
  'subscription.created': ShieldCheck,
};

/**
 * NotificationItem (W6-F4-S1) — single row shown in the notification center
 * and the bell dropdown. Renders icon, title, body, relative timestamp, and
 * the source `kind`. Mark-read button hides the unread dot.
 */
export function NotificationItem({
  notification,
  onMarkRead,
  onDismiss,
  onSelect,
}: NotificationItemProps): JSX.Element {
  const { t } = useTranslation();
  const Icon = KIND_ICON[notification.kind] ?? Bell;
  const unread = notification.read_at == null;
  const ts = notification.created_at;
  const relative = formatRelative(ts);
  const isInvitation = bucketFor(notification) === 'Invitation';
  const actionButtons = isInvitation ? readActionButtons(notification) : [];

  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();

  function handleClick(): void {
    onSelect?.(notification);
  }

  function handleAction(
    button: ActionButtonPayload,
    e: React.MouseEvent,
  ): void {
    e.stopPropagation();
    const action: InvitationAction = {
      url: button.url,
      method: button.method,
      body: button.body,
    };
    // Heuristic: the first primary button is treated as Accept; outline as
    // Decline. Both hooks call through the same transport — the variant only
    // exists for query-key invalidation if we ever split them.
    const isAccept = button.variant !== 'outline';
    const mutation = isAccept ? accept : decline;
    mutation.mutate(action, {
      onSuccess: () => {
        toast.success(
          t(
            isAccept
              ? 'notifications.toast.invitationAccepted'
              : 'notifications.toast.invitationDeclined',
          ),
        );
      },
      onError: () => {
        toast.error(t('notifications.toast.invitationFailed'));
      },
    });
  }

  return (
    <div
      role="listitem"
      data-testid={`notification-${notification.id}`}
      data-unread={unread ? 'true' : 'false'}
      className={cn(
        'flex items-start gap-3 rounded-md border border-transparent px-3 py-2.5',
        unread
          ? 'bg-primary/5 hover:bg-primary/10'
          : 'hover:bg-accent/40',
      )}
      onClick={handleClick}
    >
      <span
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          unread ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate text-sm font-medium text-foreground">
            {notification.title}
          </p>
          <time
            dateTime={ts}
            className="shrink-0 text-xs text-muted-foreground"
            title={ts}
          >
            {relative}
          </time>
        </div>
        {notification.body ? (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {notification.body}
          </p>
        ) : null}
        {actionButtons.length > 0 ? (
          <div
            className="mt-2 flex flex-wrap items-center gap-2"
            data-testid={`notification-actions-${notification.id}`}
          >
            {actionButtons.map((b, idx) => (
              <Button
                key={`${b.url}-${idx}`}
                size="sm"
                variant={b.variant === 'outline' ? 'outline' : 'default'}
                disabled={accept.isPending || decline.isPending}
                onClick={(e) => handleAction(b, e)}
                data-testid={`notification-action-${notification.id}-${idx}`}
              >
                {b.label}
              </Button>
            ))}
          </div>
        ) : null}
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="rounded bg-muted/70 px-1.5 py-0.5 font-mono text-[10px] text-foreground/70">
            {notification.kind}
          </span>
          {unread ? (
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" aria-label={t('notifications.unread')} />
          ) : null}
          <div className="ml-auto flex items-center gap-1">
            {unread && onMarkRead ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkRead(notification.id);
                }}
              >
                {t('notifications.markRead')}
              </Button>
            ) : null}
            {onDismiss ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px]"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(notification.id);
                }}
              >
                {t('notifications.dismiss')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Lightweight relative-time formatter. Avoids date-fns to keep bundle size
 * tight — date-fns isn't already in the deps tree per W1's package.json.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.round((now.getTime() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86_400) return `${Math.round(diffSec / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
